import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPost, apiPut, fmtCurrency, fmtDate } from "@/utils/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertCircle, Plus, Send, Loader2, Eye, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function CustomsDuty() {
  const [boes, setBoes]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detailBoe, setDetailBoe]   = useState(null);
  const [showReconcile, setShowReconcile] = useState(false);
  const [actioning, setActioning]   = useState({});

  // Create form state
  const [form, setForm] = useState({ boe_number: "", boe_date: "", shipment_code: "", bilty_number: "", total_bcd: 0, total_sws: 0, total_cess: 0, total_igst: 0 });

  // Reconcile form
  const [dutyLines, setDutyLines] = useState([]);
  const [customsVendorId, setCustomsVendorId] = useState("");

  const fetchBoes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet("/customs-duty");
      setBoes(data.boe_payments || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBoes(); }, [fetchBoes]);

  const handleCreate = async () => {
    if (!form.boe_number || !form.boe_date || !form.shipment_code) {
      toast.error("BOE Number, Date, and Shipment Code are required");
      return;
    }
    setActioning((a) => ({ ...a, create: true }));
    try {
      await apiPost("/customs-duty", form);
      toast.success("BOE created");
      setShowCreate(false);
      setForm({ boe_number: "", boe_date: "", shipment_code: "", bilty_number: "", total_bcd: 0, total_sws: 0, total_cess: 0, total_igst: 0 });
      fetchBoes();
    } catch (e) { toast.error(e.message); }
    finally { setActioning((a) => ({ ...a, create: false })); }
  };

  const openReconcile = (boe) => {
    setDetailBoe(boe);
    // Pre-fill duty lines from shipment GRNs (blank template if none)
    setDutyLines([{
      product_id: "",
      material_description: "",
      grn_number: boe.shipment_code + "-10",
      bcd_amount: 0,
      sws_amount: 0,
      cess_amount: 0,
      igst_amount: 0,
    }]);
    setCustomsVendorId("CUSTOMS-AUTH");
    setShowReconcile(true);
  };

  const updateDutyLine = (idx, field, value) => {
    setDutyLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addDutyLine = () =>
    setDutyLines((prev) => [...prev, { product_id: "", material_description: "", grn_number: "", bcd_amount: 0, sws_amount: 0, cess_amount: 0, igst_amount: 0 }]);

  const handleReconcile = async () => {
    if (!detailBoe) return;
    setActioning((a) => ({ ...a, reconcile: true }));
    try {
      const updated = await apiPost(`/customs-duty/${detailBoe.boe_id}/reconcile`, {
        duty_lines: dutyLines,
        customs_vendor_id: customsVendorId,
      });
      toast.success("Reconciled successfully");
      setShowReconcile(false);
      setDetailBoe(updated);
      fetchBoes();
    } catch (e) {
      if (e.message.includes("Tolerance breach")) {
        toast.error(e.message, { duration: 6000 });
      } else {
        toast.error(e.message);
      }
    } finally { setActioning((a) => ({ ...a, reconcile: false })); }
  };

  const handlePost = async (boeId) => {
    setActioning((a) => ({ ...a, [boeId]: true }));
    try {
      const updated = await apiPost(`/customs-duty/${boeId}/post`, {});
      toast.success("Posted to SAP");
      if (detailBoe?.boe_id === boeId) setDetailBoe(updated);
      fetchBoes();
    } catch (e) { toast.error(e.message); }
    finally { setActioning((a) => ({ ...a, [boeId]: false })); }
  };

  const totalDuty = (boe) =>
    (boe.total_bcd || 0) + (boe.total_sws || 0) + (boe.total_cess || 0) + (boe.total_igst || 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-slate-600">{boes.length} BOE record{boes.length !== 1 ? "s" : ""}</p>
        <Button onClick={() => setShowCreate(true)} data-testid="create-boe-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white gap-2 text-[13px]">
          <Plus size={14} /> New BOE Entry
        </Button>
      </div>

      {/* BOE Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-slate-200 border-b-2 border-slate-300">
              {["BOE ID", "BOE Number", "BOE Date", "Shipment", "BCD", "SWS", "Cess", "IGST", "Total Duty", "Recon Status", "Actions"].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-bold text-slate-700 uppercase text-[11px] tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-300">
            {loading ? (
              <tr><td colSpan={11} className="py-10 text-center"><Loader2 size={18} className="animate-spin mx-auto text-slate-400" /></td></tr>
            ) : !boes.length ? (
              <tr><td colSpan={11} className="py-12 text-center text-[13px] text-slate-400">
                No BOE records. Create your first one.
              </td></tr>
            ) : boes.map((boe) => (
              <tr key={boe.boe_id} className="hover:bg-slate-50" data-testid={`boe-row-${boe.boe_id}`}>
                <td className="px-4 py-3 font-mono text-[#0052CC] font-medium">{boe.boe_id}</td>
                <td className="px-4 py-3 font-mono text-slate-800">{boe.boe_number}</td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(boe.boe_date)}</td>
                <td className="px-4 py-3 font-mono text-slate-600">{boe.shipment_code}</td>
                <td className="px-4 py-3 font-mono">{fmtCurrency(boe.total_bcd)}</td>
                <td className="px-4 py-3 font-mono">{fmtCurrency(boe.total_sws)}</td>
                <td className="px-4 py-3 font-mono">{fmtCurrency(boe.total_cess)}</td>
                <td className="px-4 py-3 font-mono">{fmtCurrency(boe.total_igst)}</td>
                <td className="px-4 py-3 font-mono font-bold text-slate-900">{fmtCurrency(totalDuty(boe))}</td>
                <td className="px-4 py-3"><StatusBadge status={boe.reconciliation_status} /></td>
                <td className="px-4 py-3 flex items-center gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => setDetailBoe(boe)} className="h-6 w-6 p-0 text-slate-400" data-testid={`view-boe-${boe.boe_id}`}>
                    <Eye size={12} />
                  </Button>
                  {boe.reconciliation_status === "pending" && (
                    <Button size="sm" onClick={() => openReconcile(boe)}
                      data-testid={`reconcile-btn-${boe.boe_id}`}
                      className="h-6 text-[11px] bg-amber-500 hover:bg-amber-600 text-white px-2 gap-1">
                      <ShieldCheck size={11} /> Reconcile
                    </Button>
                  )}
                  {boe.reconciliation_status === "reconciled" && boe.status !== "posted" && (
                    <Button size="sm" onClick={() => handlePost(boe.boe_id)} disabled={actioning[boe.boe_id]}
                      data-testid={`post-boe-${boe.boe_id}`}
                      className="h-6 text-[11px] bg-[#0052CC] hover:bg-[#0043A8] text-white px-2 gap-1">
                      {actioning[boe.boe_id] ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                      Post SAP
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create BOE Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900">New BOE Entry</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            {[
              ["BOE Number *", "boe_number", "text", "BOE/2026/001"],
              ["BOE Date *", "boe_date", "date", ""],
              ["Shipment Code *", "shipment_code", "text", "SHP-2026-001"],
              ["Bilty Number", "bilty_number", "text", "BLT-001"],
            ].map(([label, field, type, placeholder]) => (
              <div key={field} className="space-y-1.5">
                <label className="text-[12px] font-medium text-slate-700">{label}</label>
                <Input
                  type={type}
                  placeholder={placeholder}
                  value={form[field]}
                  onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                  className="text-sm border-slate-300 font-mono"
                  data-testid={`boe-${field}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-2">
            <p className="text-[11px] font-mono uppercase text-slate-400 mb-3 tracking-wider">Duty Amounts (₹)</p>
            <div className="grid grid-cols-4 gap-3">
              {[["BCD", "total_bcd"], ["SWS", "total_sws"], ["Cess", "total_cess"], ["IGST", "total_igst"]].map(([label, field]) => (
                <div key={field} className="space-y-1">
                  <label className="text-[11px] font-mono text-slate-500 uppercase">{label}</label>
                  <Input
                    type="number"
                    min="0"
                    value={form[field] || ""}
                    onChange={(e) => setForm((f) => ({ ...f, [field]: parseFloat(e.target.value) || 0 }))}
                    className="h-8 text-[12px] border-slate-300 font-mono"
                    data-testid={`boe-${field}`}
                  />
                </div>
              ))}
            </div>
            <p className="text-[12px] font-mono text-slate-600 mt-2">
              Total: <strong>{fmtCurrency((form.total_bcd || 0) + (form.total_sws || 0) + (form.total_cess || 0) + (form.total_igst || 0))}</strong>
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowCreate(false)} className="text-[13px]">Cancel</Button>
            <Button onClick={handleCreate} disabled={actioning.create} data-testid="submit-boe-button"
              className="bg-[#0052CC] hover:bg-[#0043A8] text-white gap-2 text-[13px]">
              {actioning.create ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Create BOE
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reconcile Dialog */}
      <Dialog open={showReconcile} onOpenChange={setShowReconcile}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900">Reconcile BOE – {detailBoe?.boe_number}</DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 text-[12px] text-amber-800">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <p>1% tolerance applies. Entered duty total must match BOE payment within 1%. Exceeding tolerance will <strong>block</strong> this BOE.</p>
            </div>

            <div className="flex items-center gap-3 text-[13px]">
              <span className="text-slate-500">Total paid:</span>
              <span className="font-mono font-bold text-slate-900">{fmtCurrency(detailBoe?.total_duty_paid)}</span>
            </div>

            {/* Per-GRN duty lines */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[12px] font-medium text-slate-700">Per-GRN Duty Lines</p>
                <Button variant="ghost" size="sm" onClick={addDutyLine} className="h-6 text-[12px] text-[#0052CC] gap-1">
                  <Plus size={11} /> Add Line
                </Button>
              </div>
              <div className="border border-slate-200 rounded overflow-hidden text-[11px]">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["Product ID", "Material Desc", "GRN Number", "BCD", "SWS", "Cess", "IGST"].map((h) => (
                        <th key={h} className="px-2.5 py-2 text-left font-semibold text-slate-500 uppercase text-[10px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dutyLines.map((dl, idx) => (
                      <tr key={idx}>
                        <td className="px-1.5 py-1.5">
                          <Input value={dl.product_id} onChange={(e) => updateDutyLine(idx, "product_id", e.target.value)}
                            className="h-7 text-[11px] border-slate-300 font-mono" placeholder="PROD-001" data-testid={`duty-product-${idx}`} />
                        </td>
                        <td className="px-1.5 py-1.5">
                          <Input value={dl.material_description} onChange={(e) => updateDutyLine(idx, "material_description", e.target.value)}
                            className="h-7 text-[11px] border-slate-300" placeholder="HR Steel" />
                        </td>
                        <td className="px-1.5 py-1.5">
                          <Input value={dl.grn_number} onChange={(e) => updateDutyLine(idx, "grn_number", e.target.value)}
                            className="h-7 text-[11px] border-slate-300 font-mono" placeholder="SHP-001-10" />
                        </td>
                        {["bcd_amount", "sws_amount", "cess_amount", "igst_amount"].map((field) => (
                          <td key={field} className="px-1.5 py-1.5">
                            <Input type="number" min="0" value={dl[field] || ""}
                              onChange={(e) => updateDutyLine(idx, field, parseFloat(e.target.value) || 0)}
                              className="h-7 text-[11px] border-slate-300 font-mono w-20" data-testid={`duty-${field}-${idx}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[12px] font-mono text-slate-600">
                Entered total: <strong>{fmtCurrency(dutyLines.reduce((s, d) => s + d.bcd_amount + d.sws_amount + d.cess_amount + d.igst_amount, 0))}</strong>
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-slate-700">Customs Vendor ID</label>
              <Input value={customsVendorId} onChange={(e) => setCustomsVendorId(e.target.value)}
                className="text-sm border-slate-300 font-mono max-w-xs" data-testid="customs-vendor-input" />
            </div>

            <div className="flex gap-3 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => setShowReconcile(false)} className="text-[13px]">Cancel</Button>
              <Button onClick={handleReconcile} disabled={actioning.reconcile} data-testid="submit-reconcile-button"
                className="bg-[#0052CC] hover:bg-[#0043A8] text-white gap-2 text-[13px]">
                {actioning.reconcile ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                Reconcile
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailBoe && !showReconcile} onOpenChange={() => setDetailBoe(null)}>
        <DialogContent className="max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900 flex items-center gap-3">
              {detailBoe?.boe_number}
              {detailBoe && <StatusBadge status={detailBoe.reconciliation_status} />}
            </DialogTitle>
          </DialogHeader>
          {detailBoe && (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] mt-2">
              {[
                ["Shipment", detailBoe.shipment_code],
                ["BOE Date", fmtDate(detailBoe.boe_date)],
                ["BCD", fmtCurrency(detailBoe.total_bcd)],
                ["SWS", fmtCurrency(detailBoe.total_sws)],
                ["Cess", fmtCurrency(detailBoe.total_cess)],
                ["IGST", fmtCurrency(detailBoe.total_igst)],
                ["Total Duty Paid", fmtCurrency(detailBoe.total_duty_paid)],
                ["Reconciled Total", fmtCurrency(detailBoe.reconciled_total)],
                ["SAP Invoice", detailBoe.sap_customs_invoice_id || "—"],
                ["SAP Allocation", detailBoe.sap_allocation_doc_id || "—"],
              ].map(([k, v]) => (
                <>
                  <dt key={`k-${k}`} className="text-slate-400">{k}</dt>
                  <dd key={`v-${k}`} className="font-mono font-medium text-slate-900">{v}</dd>
                </>
              ))}
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
